import { ActionIcon, Badge, Box, Button, Drawer, Group, Loader, Select, SimpleGrid, Stack, Table, Text, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconChevronLeft, IconChevronRight, IconLock, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { api, Expense, SOURCE_NAMES } from '../api';
import { draftFrom, ExpenseForm } from '../components/ExpenseForm';
import { day, dayjs, fullDay, money, monthEnd, monthName, monthStart } from '../format';
import { defaults, useSession } from '../session';

export function ExpensesPage() {
  const { me, catalog } = useSession();
  const mobile = useMediaQuery('(max-width: 48em)');
  const [month, setMonth] = useState(monthStart(new Date()));
  const [department, setDepartment] = useState<string | null>(defaults(me).departmentId);
  const [legalEntity, setLegalEntity] = useState<string | null>(null);
  const [rows, setRows] = useState<Expense[] | null>(null);
  const [editing, setEditing] = useState<Expense | 'new' | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    const q = new URLSearchParams({ from: month, to: monthEnd(month) });
    if (department) q.set('department_id', department);
    if (legalEntity) q.set('legal_entity_id', legalEntity);
    const r = await api.get<{ rows: Expense[] }>(`/expenses?${q}`);
    setRows(r.rows);
  }, [month, department, legalEntity]);

  useEffect(() => {
    load().catch((e) => notifications.show({ color: 'red', message: e.message }));
  }, [load]);

  if (!catalog) return null;

  const total = rows?.reduce((s, r) => s + Number(r.amount), 0) ?? 0;
  const shared = rows?.filter((r) => !r.legal_entity_id) ?? [];
  const noDept = rows?.filter((r) => !r.department_id) ?? [];

  const saved = (e: Expense) => {
    setEditing(null);
    notifications.show({ color: 'brand', message: `Сохранено: ${money(e.amount)}` });
    void load();
  };
  const remove = (e: Expense) =>
    modals.openConfirmModal({
      title: 'Удалить затрату?',
      children: <Text size="sm">{`${money(e.amount)} · ${e.description ?? e.category}. Удаление останется в журнале изменений.`}</Text>,
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.del(`/expenses/${e.id}`);
          setEditing(null);
          void load();
        } catch (err) {
          notifications.show({ color: 'red', message: (err as Error).message });
        }
      },
    });

  return (
    <Box p={{ base: 'md', sm: 'xl' }} maw={1200}>
      <Group justify="space-between" mb="lg" wrap="wrap" gap="sm">
        <Title order={2}>Затраты</Title>
        <Button leftSection={<IconPlus size={18} />} onClick={() => setEditing('new')}>
          Добавить затрату
        </Button>
      </Group>

      <Group mb="lg" gap="sm" wrap="wrap">
        <Group gap={4} className="mis-card" px={6} py={4}>
          <ActionIcon variant="subtle" aria-label="Предыдущий месяц" onClick={() => setMonth(monthStart(dayjs(month).subtract(1, 'month').toDate()))}>
            <IconChevronLeft size={18} />
          </ActionIcon>
          <Text fw={500} w={140} ta="center">
            {monthName(month)}
          </Text>
          <ActionIcon variant="subtle" aria-label="Следующий месяц" onClick={() => setMonth(monthStart(dayjs(month).add(1, 'month').toDate()))}>
            <IconChevronRight size={18} />
          </ActionIcon>
        </Group>
        <Select
          placeholder="Все подразделения"
          aria-label="Подразделение"
          clearable
          data={catalog.departments.map((d) => ({ value: d.id, label: d.name }))}
          value={department}
          onChange={setDepartment}
          w={{ base: '100%', sm: 240 }}
        />
        <Select
          placeholder="Оба ООО"
          aria-label="ООО"
          clearable
          data={catalog.legalEntities.map((l) => ({ value: l.id, label: l.name }))}
          value={legalEntity}
          onChange={setLegalEntity}
          w={{ base: '100%', sm: 200 }}
        />
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="lg" spacing={mobile ? 8 : 'md'}>
        <Summary compact={mobile} title={`Итого за ${monthName(month).toLowerCase()}`} value={money(total)} note={`${rows?.length ?? 0} строк`} />
        <Summary compact={mobile} title="Общие, делятся между ООО" value={money(shared.reduce((s, r) => s + Number(r.amount), 0))} note={`${shared.length} строк`} />
        <Summary
          compact={mobile}
          title="Без подразделения"
          value={money(noDept.reduce((s, r) => s + Number(r.amount), 0))}
          note={noDept.length ? `${noDept.length} строк — уточните отдел` : 'всё разнесено'}
          warn={noDept.length > 0}
        />
      </SimpleGrid>

      <Box className="mis-card" style={{ overflow: 'hidden' }}>
        {rows === null ? (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        ) : rows.length === 0 ? (
          <Text c="dimmed" p="xl" ta="center">
            За этот месяц затрат нет
          </Text>
        ) : mobile ? (
          <Stack gap={0}>
            {rows.map((r) => (
              <Group key={r.id} justify="space-between" wrap="nowrap" px="md" py="sm" className="mis-row" style={{ borderTop: '1px solid var(--mis-border)' }} onClick={() => setEditing(r)}>
                <div style={{ minWidth: 0 }}>
                  <Text fw={500} truncate>
                    {r.description || r.category}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {day(r.op_date)} · {r.department ?? 'без подразделения'} · {r.legal_entity}
                  </Text>
                </div>
                <Text fw={600} style={{ whiteSpace: 'nowrap' }}>
                  {money(r.amount)}
                </Text>
              </Group>
            ))}
          </Stack>
        ) : (
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Дата</Table.Th>
                <Table.Th>Назначение и статья</Table.Th>
                <Table.Th>Подразделение</Table.Th>
                <Table.Th>ООО</Table.Th>
                <Table.Th ta="right">Сумма</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.id} className="mis-row" onClick={() => setEditing(r)}>
                  <Table.Td c="dimmed">{day(r.op_date)}</Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      {!r.editable && <IconLock size={14} color="var(--mis-muted)" aria-label="Только просмотр" />}
                      <div>
                        <Text fw={500}>{r.description || r.category}</Text>
                        <Text size="xs" c="dimmed">
                          {r.category}
                        </Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>{r.department ?? <Text c="orange.8" size="sm">без подразделения</Text>}</Table.Td>
                  <Table.Td>{r.legal_entity_id ? r.legal_entity : <Badge variant="light">Общая</Badge>}</Table.Td>
                  <Table.Td ta="right" fw={600} style={{ whiteSpace: 'nowrap' }}>
                    {money(r.amount)}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Box>

      <Drawer
        opened={editing !== null}
        onClose={() => setEditing(null)}
        position={mobile ? 'bottom' : 'right'}
        size={mobile ? '92%' : 440}
        title={<Text fw={600}>{editing === 'new' ? 'Новая затрата' : editing ? `Затрата · ${fullDay(editing.op_date)}` : ''}</Text>}
      >
        {editing === 'new' && <ExpenseForm catalog={catalog} expenseId={null} initial={draftFrom(null, defaults(me), catalog)} onSaved={saved} />}
        {editing && editing !== 'new' && editing.editable && (
          <ExpenseForm catalog={catalog} expenseId={editing.id} initial={draftFrom(editing, defaults(me), catalog)} onSaved={saved} onDelete={() => remove(editing)} />
        )}
        {editing && editing !== 'new' && !editing.editable && <ReadOnly e={editing} />}
      </Drawer>
    </Box>
  );
}

function Summary({ title, value, note, warn, compact }: { title: string; value: string; note: string; warn?: boolean; compact?: boolean }) {
  if (compact) {
    return (
      <Group className="mis-card" px="md" py="sm" justify="space-between" wrap="nowrap">
        <div>
          <Text size="sm">{title}</Text>
          <Text size="xs" c={warn ? 'orange.8' : 'dimmed'}>
            {note}
          </Text>
        </div>
        <Text fz={18} fw={600} style={{ whiteSpace: 'nowrap' }}>
          {value}
        </Text>
      </Group>
    );
  }
  return (
    <Box className="mis-card" p="lg">
      <Text size="sm" c="dimmed">
        {title}
      </Text>
      <Text fz={26} fw={600} mt={4}>
        {value}
      </Text>
      <Text size="xs" c={warn ? 'orange.8' : 'dimmed'} mt={2}>
        {note}
      </Text>
    </Box>
  );
}

function ReadOnly({ e }: { e: Expense }) {
  const line = (k: string, v: string | null) => (
    <Group justify="space-between" py={6} style={{ borderBottom: '1px solid var(--mis-border)' }}>
      <Text c="dimmed" size="sm">
        {k}
      </Text>
      <Text size="sm" ta="right">
        {v ?? '—'}
      </Text>
    </Group>
  );
  return (
    <Stack gap="xs">
      <Text fz={28} fw={600}>
        {money(e.amount)}
      </Text>
      <Text>{e.description}</Text>
      {line('Статья', e.category)}
      {line('Подразделение', e.department)}
      {line('ООО', e.legal_entity)}
      {line('Месяц начисления', monthName(e.accrual_month))}
      {line('Оплата', e.payment_method === 'bank' ? 'Банк' : e.payment_method === 'cash' ? 'Наличные' : null)}
      {line('Источник', SOURCE_NAMES[e.source])}
      <Text size="sm" c="dimmed" mt="sm">
        {e.source === 'manual' || e.source === 'api'
          ? 'Нет прав менять эту затрату.'
          : 'Затрата загружена из таблицы. Исправьте её в таблице — иначе синхронизация вернёт старое значение.'}
      </Text>
    </Stack>
  );
}
